import React from 'react';
import { useTranslation } from 'react-i18next';
import * as Select from '@radix-ui/react-select';
import { ParentChildRelationshipType } from 'we-tree-api-client/v2';
import PersonFormFields from '@/components/person/PersonFormFields';
import {
  PARENT_CHILD_RELATIONSHIP_OPTIONS,
  type ParentChildRelationshipTypeValue
} from '@/types/parent-child-relationship';
import type { DatePrecision } from '@/utils/dateFormatting';

export interface AdditionalParentSpouseRow {
  spouseFamilyMemberId: string;
  displayName: string;
}

export interface AddFamilyMemberCreateNewFieldsProps {
  showRelationshipToParent: boolean;
  formData: {
    parentChildRelationshipType: ParentChildRelationshipTypeValue;
    givenName: string;
    middleName: string;
    surname: string;
    suffix: string;
    preferredName: string;
    gender: string;
    birthDate: string;
    birthDatePrecision: DatePrecision;
    birthPlace: string;
    deathDate: string;
    deathDatePrecision: DatePrecision;
    deathPlace: string;
    isDeceased: boolean;
  };
  errors: Record<string, string>;
  isSubmitting: boolean;
  onChange: (field: string, value: string) => void;
  onPrecisionChange: (precision: DatePrecision) => void;
  onBirthDateChange: (rawValue: string) => void;
  onDeathPrecisionChange: (precision: DatePrecision) => void;
  onDeathDateChange: (rawValue: string) => void;
  onDeceasedChange: (deceased: boolean) => void;
  /** When adding a child with spouses on the anchor parent */
  additionalParents?: {
    spouses: AdditionalParentSpouseRow[];
    choices: Record<string, { selected: boolean; relationshipType: ParentChildRelationshipTypeValue }>;
    onToggle: (spouseFamilyMemberId: string, selected: boolean) => void;
    onRelationshipType: (spouseFamilyMemberId: string, relationshipType: ParentChildRelationshipTypeValue) => void;
  };
}

const AddFamilyMemberCreateNewFields: React.FC<AddFamilyMemberCreateNewFieldsProps> = ({
  showRelationshipToParent,
  formData,
  errors,
  isSubmitting,
  onChange,
  onPrecisionChange,
  onBirthDateChange,
  onDeathPrecisionChange,
  onDeathDateChange,
  onDeceasedChange,
  additionalParents
}) => {
  const { t } = useTranslation('forms');
  return (
    <>
      <PersonFormFields
        value={{
          givenName: formData.givenName,
          middleName: formData.middleName,
          surname: formData.surname,
          suffix: formData.suffix,
          preferredName: formData.preferredName,
          gender: formData.gender,
          birthDate: formData.birthDate,
          birthDatePrecision: formData.birthDatePrecision,
          birthPlace: formData.birthPlace,
          deathDate: formData.deathDate,
          deathDatePrecision: formData.deathDatePrecision,
          deathPlace: formData.deathPlace,
          isDeceased: formData.isDeceased
        }}
        onChange={onChange}
        onPrecisionChange={onPrecisionChange}
        onBirthDateChange={onBirthDateChange}
        onDeathPrecisionChange={onDeathPrecisionChange}
        onDeathDateChange={onDeathDateChange}
        onDeceasedChange={onDeceasedChange}
        errors={errors}
        disabled={isSubmitting}
        relationshipToParent={
          showRelationshipToParent
            ? {
                value: formData.parentChildRelationshipType,
                options: PARENT_CHILD_RELATIONSHIP_OPTIONS,
                onChange: (v) => onChange('parentChildRelationshipType', v)
              }
            : undefined
        }
      />

      {additionalParents && additionalParents.spouses.length > 0 && (
        <div className="person-edit-form__section add-member-page__additional-parents">
          <h3 className="person-edit-form__section-title">Also add as child of spouse</h3>
          <p className="person-edit-form__hint add-member-page__additional-parents-hint">
            Are any of these spouses also parents of this child? If so, select them and set their relationship to the
            child.
          </p>
          <div className="add-member-page__additional-parents-list">
            {additionalParents.spouses.map(({ spouseFamilyMemberId, displayName }) => {
              const choice = additionalParents.choices[spouseFamilyMemberId] ?? {
                selected: false,
                relationshipType: ParentChildRelationshipType.Biological as ParentChildRelationshipTypeValue
              };
              return (
                <div
                  key={spouseFamilyMemberId}
                  className={`add-member-page__additional-parent-row ${choice.selected ? 'add-member-page__additional-parent-row--selected' : ''}`}
                >
                  <label className="person-edit-form__checkbox-label add-member-page__additional-parent-checkbox">
                    <input
                      type="checkbox"
                      checked={choice.selected}
                      onChange={(e) => additionalParents.onToggle(spouseFamilyMemberId, e.target.checked)}
                      disabled={isSubmitting}
                      className="person-edit-form__checkbox"
                    />
                    <span className="add-member-page__additional-parent-name">{displayName}</span>
                  </label>
                  {choice.selected && (
                    <Select.Root
                      value={choice.relationshipType}
                      onValueChange={(v) =>
                        additionalParents.onRelationshipType(
                          spouseFamilyMemberId,
                          v as ParentChildRelationshipTypeValue
                        )
                      }
                      disabled={isSubmitting}
                    >
                      <Select.Trigger
                        className="form-select-trigger form-select-trigger--compact"
                        aria-label={t('relationships.relationshipTypeAria', { name: displayName })}
                      >
                        <Select.Value />
                      </Select.Trigger>
                      <Select.Portal>
                        <Select.Content className="form-select-content" position="popper" sideOffset={4}>
                          {PARENT_CHILD_RELATIONSHIP_OPTIONS.map((opt) => (
                            <Select.Item key={opt.value} value={opt.value} className="form-select-item">
                              <Select.ItemText>{opt.label}</Select.ItemText>
                            </Select.Item>
                          ))}
                        </Select.Content>
                      </Select.Portal>
                    </Select.Root>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
};

export default AddFamilyMemberCreateNewFields;
